import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Modal from './Modal';

/**
 * App-wide confirm dialog — the themed replacement for window.confirm().
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete import?', message: '…', danger: true }))) return;
 *
 * Accepts a plain string as shorthand for { message }. Resolves true on
 * confirm, false on cancel/escape/backdrop — same contract as
 * window.confirm, so call sites convert mechanically.
 */

const ConfirmContext = createContext(() => Promise.resolve(false));

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    resolver.current = resolve;
    setDialog({
      title: 'Are you sure?',
      confirmLabel: 'Confirm',
      danger: false,
      ...(typeof opts === 'string' ? { message: opts } : opts),
    });
  }), []);

  const close = (result) => {
    setDialog(null);
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <Modal
          title={dialog.title}
          onClose={() => close(false)}
          maxWidth={460}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => close(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                autoFocus
                style={dialog.danger ? { background: '#dc2626', borderColor: '#dc2626' } : undefined}
                onClick={() => close(true)}
              >
                {dialog.confirmLabel}
              </button>
            </>
          }
        >
          <div style={{ fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>
            {dialog.message}
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() { return useContext(ConfirmContext); }
